// @flow

import _ from 'lodash';
import { rem } from 'polished';
import React from 'react';
import styled from 'styled-components';

import { HEADER_Z_INDEX } from 'client/constants/styles';
import BrandLogo from 'client/components/brandLogo';
import BREAKPOINTS from 'client/constants/styles/breakpoints';
import COLORS from 'client/constants/colors';
import HealthEntryPoint from 'client/components/ifpApp/healthEntryPoint';
import HighlightHeader from 'client/components/highlightHeader';
import LoginLogoutLink from 'client/components/nav/loginLogoutLink';
import Translate from 'client/components/translate';
import MobileLoginLogoutBtn from 'client/components/homeNav/mobileLoginLogoutBtnContainer';

type Props = {
  contentfulData: Object,
  heroImage: string,
  onSearch: Function,
  notShowNav: boolean,
  siteConfigs: Object,
  sectionName: string,
};

const HeroContainer = styled.div`
  color: ${COLORS.NAVY_1};
  background-color: ${COLORS.BLUE_5};
  position: relative;
  overflow: hidden;
  .button.cta-button.continue,
  .button.cta-button.view-dashboard {
    margin: 1rem auto;
  }
  .center-col-xlarge {
    position: relative;
  }
`;

const HeroContentContainer = styled.div`
  height: 100%;
  max-width: 50%;
  overflow: visible;
  padding: 2rem 1rem 3rem;
  @media screen and (max-width: ${BREAKPOINTS.MEDIUM_LARGE}) {
    max-width: 100%;
  }
  @media screen and (max-width: ${BREAKPOINTS.MEDIUM}) {
    padding: 2rem 1rem 2rem;
  }
`;

const HeroHeaderWrapper = styled.h1`
  font-family: 'Lato', 'Open Sans', Helvetica, sans-serif;
  font-weight: 900;
  position: relative;
  width: 110%;
  z-index: 5;
  padding-bottom: 1rem;

  @media screen and (max-width: ${BREAKPOINTS.MEDIUM_LARGE}) {
    width: 100%;
  }
`;

const HeroImageContainer = styled.div`
  background: linear-gradient(
      to bottom,
      rgba(55, 55, 55, 0.5) 0%,
      rgba(142, 142, 142, 0) 20%,
      rgba(124, 124, 124, 0) 25%,
      rgba(0, 0, 0, 0) 100%
    ),
    url(${props => props.img});
  background-position: bottom right;
  background-repeat: no-repeat;
  background-size: cover;
  position: absolute;
  top: 0;
  right: 0;
  width: 50%;
  float: right;

  @media screen and (max-width: ${BREAKPOINTS.MEDIUM_LARGE}) {
    display: none;
  }
`;

const ImageMask = styled.div`
  border-top: ${rem(600)} solid ${COLORS.BLUE_5};
  border-right: ${rem(140)} solid transparent;
  height: ${rem(600)};
  width: ${rem(140)};

  @media screen and (max-width: ${BREAKPOINTS.LARGE}) {
    border-top: ${rem(700)} solid ${COLORS.BLUE_5};
    height: ${rem(700)};
  }
  @media screen and (max-width: ${BREAKPOINTS.MEDIUM_LARGE}) {
    display: none;
  }
`;

const LogoWrapper = styled.div`
  margin-bottom: 2rem;

  .header-logo-container {
    display: inline-block;
    img {
      height: ${rem(31)};
    }
    &.white-logo img {
      filter: brightness(0) opacity(60%);
    }
    img.partner-logo-img {
      filter: none;
    }
  }
  @media screen and (max-width: ${BREAKPOINTS.MEDIUM}) {
    max-width: calc(100% - 100px);
    .header-logo-container {
      img {
        height: auto;
        max-height: ${rem(31)};
      }
      img.stride-logo-img {
        max-width: 40%;
      }
      img.partner-logo-img {
        max-width: 55%;
      }
    }
  }
`;

export const CheckListItem = styled.div`
  margin-left: 2rem;
  padding-bottom: 0.5rem;
  position: relative;

  &.icon-ok:before {
    position: absolute;
    left: -2rem;
    top: ${rem(4)};
    color: ${COLORS.BLUE_1};
  }
`;

const CheckMarkItem = styled.div`
  margin-left: 2.5rem;
  padding-bottom: 0.5rem;
  position: relative;

  .icon-ok-circled:before {
    position: absolute;
    color: ${COLORS.BLUE_1};
    left: -2.5rem;
    font-size: 1.5rem;
  }
`;

// overwrite component default style
const NavBar = styled.div`
  display: flex;
  position: absolute;
  right: 0;
  margin-top: 1.5rem;
  z-index: ${HEADER_Z_INDEX};

  .nav-link {
    font-size: 1rem;
    font-weight: bold;
    color: black;
    display: inline-block;
    padding: 0 1rem;
    .button {
      vertical-align: unset;
      line-height: ${rem(24)};
      border-radius: 0;
    }
    a {
      color: white;
    }
    a:hover {
      color: white;
      padding-bottom: 0.25rem;
      border-bottom: ${rem(2)} solid ${COLORS.WHITE};
    }
    @media screen and (max-width: ${BREAKPOINTS.SMALL}) {
      a,
      a:hover,
      a:visited {
        color: ${COLORS.BLUE_2};
        border: ${rem(1)} solid ${COLORS.BLUE_2};
        padding: 0.75rem 1rem;
      }
      .button {
        border-radius: ${rem(3)};
        line-height: 1;
      }
    }
  }
`;

const MobileLoginLogoutBtnWrapper = styled.div`
  .mobile-login-button {
    display: none;
    position: absolute;
    right: 0;
    top: 0;
    margin: 1.5rem 1rem 0 0;
    padding: 0.75rem 1rem;
    border: solid 1px ${COLORS.WHITE};
    background-color: transparent;
    &:focus,
    &:hover,
    &:active {
      background-color: transparent;
    }
    @media screen and (max-width: ${BREAKPOINTS.LARGE}) {
      display: block;
      z-index: ${HEADER_Z_INDEX};
    }
    @media screen and (max-width: ${BREAKPOINTS.MEDIUM_LARGE}) {
      color: ${COLORS.BLUE_2} !important;
      border: solid 1px ${COLORS.BLUE_2};
    }
  }
`;

const HealthHero = (props: Props) => {
  const {
    contentfulData: { heroHeader, heroContent },
    heroImage,
    siteConfigs,
    onSearch,
    notShowNav,
    sectionName,
  } = props;

  const brandHealthHeroHeader = _.get(siteConfigs, 'brandSiteConfig.healthHeroHeader');
  const hero = _.isEmpty(brandHealthHeroHeader) ? heroHeader : brandHealthHeroHeader;

  return (
    <HeroContainer className="health-hero-container">
      <div className="center-col-xlarge">
        {!notShowNav && (
          <NavBar>
            <LoginLogoutLink />
          </NavBar>
        )}
        {notShowNav && (
          <MobileLoginLogoutBtnWrapper>
            <MobileLoginLogoutBtn />
          </MobileLoginLogoutBtnWrapper>
        )}
        <HeroContentContainer>
          <LogoWrapper>
            <BrandLogo useTeal {...props} />
          </LogoWrapper>
          <HeroHeaderWrapper>
            <HighlightHeader {...hero} />
          </HeroHeaderWrapper>
          {_.map(heroContent.value, item => (
            <CheckListItem className="icon-ok" key={item}>
              {item}
            </CheckListItem>
          ))}
          <HealthEntryPoint id={sectionName} onClick={value => onSearch(value, sectionName)} />
          <CheckMarkItem>
            <Translate className="icon-ok-circled" fallback={heroContent.officialPartner} />
          </CheckMarkItem>
        </HeroContentContainer>
      </div>
      <HeroImageContainer img={heroImage}>
        <ImageMask />
      </HeroImageContainer>
    </HeroContainer>
  );
};

HealthHero.defaultProps = {
  notShowNav: false,
};

export default HealthHero;
